import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import * as XLSX from "xlsx";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DataContext from "../../context/DataContext";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
);

interface DataPoint {
  vs: number;
  tvd: number;
}

const labelOptions = [
  {
    id: 0,
    name: "Rod Pump",
    label: "Seating Nipple",
  },
  {
    id: 1,
    name: "ESP",
    label: "Sensor Depth",
  },
  {
    id: 2,
    name: "Gas Lift",
    label: "End of tubing",
  },
  {
    id: 3,
    name: "PCP",
    label: "Pump Depth",
  },
];

export const VSvsTVDChart: React.FC = () => {
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvInput, setCsvInput] = useState("");
  const [csvError, setCsvError] = useState<string | null>(null);
  const [annotations, setAnnotations] = useState<
    { vs: number; tvd: number; label: string }[]
  >([]);

  const handleTemplateDownload = () => {
    const link = document.createElement("a");
    link.href = "/templates/TEMPLATE_TVDVS.xlsx"; // public path
    link.download = "template.xlsx";
    link.click();
  };
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  const { onUpdateBasicInfo, data } = useContext(DataContext);

  useEffect(() => {
    data.basicInfo.tvdVs && setDataPoints(data.basicInfo.tvdVs || []);
  }, [data.basicInfo.tvdVs]);

  useEffect(() => {
    data.basicInfo.tvdVsAnnotations &&
      setAnnotations(data.basicInfo.tvdVsAnnotations || []);
  }, [data.basicInfo.tvdVsAnnotations]);

  useEffect(() => {
    const labelFound = labelOptions.find(
      (option) => option.id === data.basicInfo.sla.id,
    )?.label;
    labelFound &&
      setAnnotations((prev) => {
        const updated = [...prev];
        updated[0] = {
          vs: 0,
          tvd: data.basicInfo.mdDepth || 0,
          label: prev[0]?.label,
        };
        onUpdateBasicInfo("tvdVsAnnotations", updated);
        return updated;
      });
  }, [data.basicInfo.mdDepth, data.basicInfo.sla.id]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const arrayBuffer = evt.target?.result;
      if (!arrayBuffer) return;
      const wb = XLSX.read(arrayBuffer, { type: "array" });

      const sheetName = wb.SheetNames.find(
        (name) => name.toLowerCase() === "sheet1",
      );
      const ws = sheetName ? wb.Sheets[sheetName] : wb.Sheets[wb.SheetNames[0]];
      const jsonData: any[] = XLSX.utils.sheet_to_json(ws, { header: 1 });

      // Extract VS (ft) and TVD (ftKB) from columns A and B (index 0 and 1)
      const points: DataPoint[] = jsonData
        .slice(1) // skip header
        .map((row) => ({
          vs: parseFloat(row[0]),
          tvd: parseFloat(row[1]),
        }))
        .filter((point) => !isNaN(point.vs) && !isNaN(point.tvd));

      setDataPoints(points);
      onUpdateBasicInfo("tvdVs", points);
    };
    reader.readAsArrayBuffer(file);
  };

  const annotationColors = [
    { backgroundColor: "rgba(25, 118, 210, 0.7)", borderColor: "#1976d2" }, // blue
    { backgroundColor: "rgba(255, 0, 0, 0.7)", borderColor: "red" }, // red
    { backgroundColor: "rgba(128, 128, 128, 0.7)", borderColor: "gray" }, // gray
  ];

  const chartData = {
    labels: dataPoints.map((point) => point.tvd),
    datasets: [
      {
        label: "VS vs TVD",
        data: dataPoints.map((point) => ({
          x: point.vs,
          y: point.tvd,
        })),
        borderColor: "#1976d2",
        backgroundColor: "rgba(25, 118, 210, 0.4)",
        fill: false,
        tension: 0.3,
        pointRadius: 0, // smooth line only
      },
      ...annotations.map((annotation, i) => ({
        label: annotation.label || "Annotation",
        data: [{ x: annotation.vs, y: annotation.tvd }],
        backgroundColor:
          annotationColors[i]?.backgroundColor || "rgba(0,0,0,0.5)",
        borderColor: annotationColors[i]?.borderColor || "black",
        pointRadius: 5,
        pointHoverRadius: 7,
        pointStyle: "circle",
      })),
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "linear" as const,
        title: { display: true, text: "VS (ft)" },
        ticks: {
          stepSize: 1000,
        },
      },
      y: {
        title: { display: true, text: "TVD (ftKB)" },
        reverse: true, // VS increases downward if needed
      },
    },
  };

  return (
    <Card sx={{ margin: "auto", mt: 4, p: 2, width: "90%" }}>
      <CardContent>
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <Typography variant="h6" gutterBottom>
            Upload Excel to Visualize VS vs TVD
          </Typography>
          <Button variant="contained" component="label">
            Upload Excel
            <input
              hidden
              type="file"
              accept=".xlsx"
              onChange={handleFileUpload}
            />
          </Button>

          <Button
            variant="contained"
            component="label"
            onClick={handleTemplateDownload}
            style={{ marginLeft: 16 }}
          >
            Download Template
          </Button>
          <Button variant="outlined" onClick={() => setCsvModalOpen(true)}>
            Paste CSV Data
          </Button>
        </div>
        <Dialog
          open={csvModalOpen}
          onClose={() => setCsvModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Paste TVD,VS Data</DialogTitle>
          <DialogContent>
            <TextField
              label="Paste TVD, VS data"
              multiline
              minRows={6}
              fullWidth
              error={!!csvError}
              helperText={
                csvError ||
                "Expected format: TVD and VS per line, separated by space or '|'. Use period for thousands and comma for decimals (e.g. 1.200,45)"
              }
              variant="outlined"
              placeholder={`e.g.\n1.200,00 0,00\n1.250,90 | -0,50`}
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCsvModalOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                const lines = csvInput.trim().split("\n");
                const parsed: DataPoint[] = [];
                let hasError = false;

                for (const [i, line] of lines.entries()) {
                  const parts = line.trim().split(/[\s|]+/); // split by space(s) or pipe

                  if (parts.length !== 2) {
                    setCsvError(`Invalid format on line ${i + 1}: "${line}"`);
                    hasError = true;
                    break;
                  }

                  const parseEuroNumber = (str: string): number =>
                    parseFloat(str.replace(/\./g, "").replace(",", "."));

                  const tvd = parseEuroNumber(parts[0]);
                  const vs = parseEuroNumber(parts[1]);

                  if (isNaN(tvd) || isNaN(vs)) {
                    setCsvError(`Invalid number on line ${i + 1}: "${line}"`);
                    hasError = true;
                    break;
                  }

                  parsed.push({ tvd, vs });
                }

                if (!hasError) {
                  setDataPoints(parsed);
                  onUpdateBasicInfo("tvdVs", parsed);
                  setCsvModalOpen(false);
                  setCsvError(null);
                }
              }}
              variant="contained"
            >
              Import
            </Button>
          </DialogActions>
        </Dialog>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginTop: 16,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{ display: "flex", flexDirection: "row", gap: 8 }}
            >
              <Typography variant="subtitle2">Label {i + 1}</Typography>
              <TextField
                label="Name"
                variant="outlined"
                size="small"
                value={
                  annotations[i]?.label ||
                  "SN Depth at 7.783' MD with an inclination of 1.04° and a DLS of 0.05°/100ft"
                }
                onChange={(e) => {
                  const updated = [...annotations];
                  updated[i] = { ...updated[i], label: e.target.value || "" };
                  setAnnotations(updated);
                  onUpdateBasicInfo("tvdVsAnnotations", updated);
                }}
                style={{ marginBottom: 4, width: 600 }}
              />
              <TextField
                label="VS"
                type="number"
                variant="outlined"
                size="small"
                value={annotations[i]?.vs ?? ""}
                onChange={(e) => {
                  const updated = [...annotations];
                  updated[i] = {
                    ...updated[i],
                    vs: parseFloat(e.target.value) || 0,
                  };
                  setAnnotations(updated);
                  onUpdateBasicInfo("tvdVsAnnotations", updated);
                }}
                style={{ marginBottom: 4 }}
              />
              <TextField
                label="TVD"
                type="number"
                variant="outlined"
                size="small"
                value={annotations[i]?.tvd ?? ""}
                onChange={(e) => {
                  const updated = [...annotations];
                  updated[i] = {
                    ...updated[i],
                    tvd: parseFloat(e.target.value) || 0,
                  };
                  setAnnotations(updated);
                  onUpdateBasicInfo("tvdVsAnnotations", updated);
                }}
              />
            </div>
          ))}
        </div>

        {dataPoints.length > 0 && (
          <div style={{ marginTop: 24, height: "600px", width: "100%" }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
